// Development-only page the browser tests drive: the review form on the
// left and the live preview on the right, sharing one resume the way the
// job page does, with no backend behind it. Exposes the state on
// window.__harness so a test can read what an edit produced.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  SAMPLE_RESUME,
  type AgpResume,
  type SourceRedactions,
  type SourceTextDoc,
  type SourceTextEdits,
  type TemplateDefinition,
} from "@agp/shared";
import { TemplatePreview } from "@/components/templates/TemplatePreview";
import type { AppendixSource } from "@/components/preview/AppendixPages";
import { ContentLinesEditor, ExperienceEditor } from "@/components/review/editors";
import { TabRail, TAB_SECTIONS, tabOrder } from "@/components/review/TabRail";
import { useResumeHistory } from "@/hooks/useResumeHistory";

const HARNESS_TEMPLATE: TemplateDefinition = {
  id: "harness",
  version: 1,
  name: "Harness",
  description: "Browser-test template",
  fonts: { body: "Georgia", heading: "Georgia", bodyPt: 10.5, namePt: 21, headingPt: 12 },
  colors: { heading: "5B3A29", text: "1A1A1A", subtle: "6B5E55" },
  page: { size: "A4", marginTop: 1080, marginBottom: 1080, marginLeft: 1260, marginRight: 1260 },
  dateColumnTwips: 2350,
  dateFormat: "MMM.'yy",
  branding: {
    logoFile: "logo.png",
    logoSize: { width: 150, height: 48 },
    watermarkFile: "watermark.png",
    watermarkSize: { width: 420, height: 420 },
    companyName: "ASCENT GLOBAL PARTNERS",
    footerLines: ["Ascent Global Partners — Tokyo"],
  },
  sections: [
    { key: "candidateReport", label: "Candidate Report" },
    { key: "personalDetails", label: "Personal Details" },
    { key: "consultantSummary", label: "Consultant Summary" },
    { key: "summary", label: "Profile" },
    { key: "experience", label: "Experience" },
    { key: "education", label: "Education" },
    { key: "coreCompetencies", label: "Key Strengths" },
    { key: "certifications", label: "Certifications" },
    { key: "technicalSkills", label: "Systems & Tools" },
    { key: "otherSections", label: "Additional Information" },
  ],
  emphasizeAchievements: true,
};

/** A long resume so the preview spans several pages. */
function fixtureResume(): AgpResume {
  const base = structuredClone(SAMPLE_RESUME);
  base.candidate.summaryBullets = [
    { text: "Senior product leader with fifteen years across consumer goods and retail.", kind: "para" },
    { text: "Led three category relaunches.", kind: "bullet", level: 0, marker: "dot" },
    { text: "Grew the snacks line to JPY 2bn.", kind: "bullet", level: 1, marker: "circle" },
  ];
  const padded = base.experience.map((e, i) => ({
    ...e,
    roles: e.roles.map((r) => ({
      ...r,
      bullets: [
        ...r.bullets,
        ...Array.from({ length: 6 }, (_, k) => ({
          text: `Filler achievement ${i + 1}.${k + 1}: delivered a cross-functional programme on time and under budget with measurable results.`,
          kind: "bullet" as const,
        })),
      ],
    })),
  }));
  base.experience = [...padded, ...padded.map((e) => ({ ...e, employer: `${e.employer} (earlier)` }))];
  return base;
}

function pagePng(color: string): Promise<string> {
  const canvas = document.createElement("canvas");
  canvas.width = 620;
  canvas.height = 877;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000";
  ctx.font = "28px sans-serif";
  ctx.fillText("履歴書 sample page", 40, 60);
  return Promise.resolve(canvas.toDataURL("image/png"));
}

const PAGE_LINES = [
  { y: 0.04, h: 0.04 },
  { y: 0.08, h: 0.03 },
  { y: 0.11, h: 0.03 },
];

const TEXT_DOC: SourceTextDoc = {
  defaults: { font: "MS Mincho", fontPt: 10.5 },
  blocks: [
    { kind: "para", id: 0, text: "職務経歴書", style: { bold: true, fontPt: 14, align: "center" } },
    { kind: "para", id: 1, text: "氏名：佐藤 由理", style: { align: "right" } },
    {
      kind: "table",
      bordered: true,
      widths: [25, 75],
      rows: [
        [{ span: 1, paras: [{ id: 2, text: "期間" }] }, { span: 1, paras: [{ id: 3, text: "業務内容" }] }],
        [{ span: 1, paras: [{ id: 4, text: "2024年7月〜2024年10月" }] }, { span: 1, paras: [{ id: 5, text: "連結財務諸表作成" }, { id: 6, text: "" }] }],
      ],
    },
    { kind: "para", id: 7, text: "・単体・連結決算", bullet: "•", style: { indentLeft: 21, indentFirst: -10.5 } },
    { kind: "para", id: 8, text: "以上", style: { align: "right", spaceBefore: 12 } },
  ],
};

declare global {
  interface Window {
    __harness?: {
      resume: () => AgpResume;
      redactions: () => SourceRedactions;
      textEdits: () => SourceTextEdits;
      setResume: (r: AgpResume) => void;
    };
  }
}

export function EditorHarness() {
  const history = useResumeHistory(fixtureResume());
  const resume = history.resume!;
  const [pages, setPages] = useState<AppendixSource["pages"]>(null);
  const [redactions, setRedactions] = useState<SourceRedactions>({ removedPages: [], boxes: [] });
  const [textEdits, setTextEdits] = useState<SourceTextEdits>({ texts: {}, removed: [] });

  useEffect(() => {
    void Promise.all([pagePng("#fde68a"), pagePng("#bfdbfe")]).then((urls) =>
      setPages(urls.map((url, index) => ({ index, url, width: 1240, height: 1754, lines: PAGE_LINES }))),
    );
  }, []);

  const commit = useCallback((next: AgpResume, key: string | null) => history.apply(next, key), [history]);

  useEffect(() => {
    window.__harness = {
      resume: () => resume,
      redactions: () => redactions,
      textEdits: () => textEdits,
      setResume: (r) => history.reset(r),
    };
  }, [resume, redactions, textEdits, history]);

  const appendix = useMemo<AppendixSource[]>(
    () => [
      { id: "src-1", filename: "履歴書.pdf", pages, redactions, onChange: setRedactions },
      {
        id: "src-2",
        filename: "職務経歴書.docx",
        pages: null,
        redactions: { removedPages: [], boxes: [] },
        view: "text",
        text: { doc: TEXT_DOC, textEdits, onChange: setTextEdits },
      },
    ],
    [pages, redactions, textEdits],
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16, padding: 16, minHeight: "100vh" }}>
      <div data-testid="form" style={{ overflow: "auto", maxHeight: "95vh" }}>
        <TabRail
          active="summary"
          onSelect={() => undefined}
          attention={{}}
          order={tabOrder(resume.sectionOrder)}
          onReorder={(keys) => commit({ ...resume, sectionOrder: keys.flatMap((k) => TAB_SECTIONS[k]) }, null)}
        />
        <h3>Summary</h3>
        <ContentLinesEditor
          items={resume.candidate.summaryBullets}
          onChange={(summaryBullets) =>
            commit({ ...resume, candidate: { ...resume.candidate, summaryBullets } }, "summaryBullets")
          }
        />
        <h3>Experience</h3>
        <ExperienceEditor items={resume.experience} onChange={(experience) => commit({ ...resume, experience }, "experience")} />
      </div>
      <div data-testid="preview">
        <TemplatePreview
          definition={HARNESS_TEMPLATE}
          logoUrl={null}
          watermarkUrl={null}
          includeContact
          resume={resume}
          appendix={appendix}
          edit={{ onChange: commit, onUndo: () => history.undo(), onRedo: () => history.redo() }}
        />
      </div>
    </div>
  );
}
